import React, { type JSX } from "react";
import type { MaintenanceMode } from "./useMaintenanceMode";
import {
  SectionHeader,
  ToggleSwitch,
  SECTION_CLASS,
  SECTION_BODY_CLASS,
  SECTION_FOOTER_CLASS,
} from "./SettingsPrimitives";

const WARNING_PATH =
  "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z";

/** Shown while maintenance mode is armed but not yet saved. */
function EnabledWarning(): JSX.Element {
  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d={WARNING_PATH} clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-yellow-700">
            <strong>Warning:</strong> Saving with maintenance mode enabled will immediately prevent
            non-admin users from accessing the application.
          </p>
        </div>
      </div>
    </div>
  );
}

/** The on/off row, with its current-state explanation. */
function MaintenanceToggleRow({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <div>
        <label htmlFor="maintenance-toggle" className="text-sm font-medium text-gray-900">
          Enable Maintenance Mode
        </label>
        <p className="text-sm text-gray-500">
          {enabled
            ? "Maintenance mode is currently ON. Non-admin users cannot access the application."
            : "Maintenance mode is currently OFF. All users can access the application."}
        </p>
      </div>
      <ToggleSwitch
        id="maintenance-toggle"
        checked={enabled}
        onChange={onChange}
        onClass="bg-red-600"
        srLabel="Toggle maintenance mode"
      />
    </div>
  );
}

/** The message users see on the maintenance page. */
function MaintenanceMessageField({
  message,
  onChange,
}: {
  message: string;
  onChange: (message: string) => void;
}): JSX.Element {
  return (
    <div>
      <label htmlFor="maintenance-message" className="block text-sm font-medium text-gray-700">
        Maintenance Message (Optional)
      </label>
      <p className="text-sm text-gray-500 mb-2">
        This message will be shown to users on the maintenance page.
      </p>
      <textarea
        id="maintenance-message"
        rows={3}
        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
        placeholder="We're currently performing scheduled maintenance. Please check back shortly."
        value={message}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** Save button, showing a spinner while the request is in flight. */
function SaveButton({ saving, onSave }: { saving: boolean; onSave: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSave}
      disabled={saving}
      className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
        saving
          ? "bg-blue-400 cursor-not-allowed"
          : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      }`}
    >
      {saving ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Saving...
        </>
      ) : (
        "Save Settings"
      )}
    </button>
  );
}

/** Locks non-admin users out of the application. */
export default function MaintenanceModeSection({
  maintenance,
}: {
  maintenance: MaintenanceMode;
}): JSX.Element {
  return (
    <div className={SECTION_CLASS}>
      <SectionHeader
        title="Maintenance Mode"
        description="When enabled, non-admin users will see a maintenance page instead of the application."
      />

      <div className={SECTION_BODY_CLASS}>
        <MaintenanceToggleRow enabled={maintenance.enabled} onChange={maintenance.setEnabled} />
        {maintenance.enabled && <EnabledWarning />}
        <MaintenanceMessageField
          message={maintenance.message}
          onChange={maintenance.setMessage}
        />
      </div>

      <div className={SECTION_FOOTER_CLASS}>
        <SaveButton saving={maintenance.saving} onSave={() => void maintenance.save()} />
      </div>
    </div>
  );
}
